import {
  useEffect,
  useRef,
  ReactElement,
  useMemo,
  Children,
  ComponentProps,
  FC,
  ReactNode,
  isValidElement,
  Ref,
} from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Button, SlideCarousel, useSlideCarousel } from '@frontend/design-system';
import { BannerCarouselProvider, useBannerCarouselContext } from './banner-carousel-context';
import { BannerCarouselSlide } from './banner-carousel-slide';
import { useBannerCarouselTracking } from './use-banner-carousel-tracking';
import '@design-system/tokens';

type BannerCarouselSlideElement = ReactElement<ComponentProps<typeof BannerCarouselSlide>>;

interface BaseCarouselProps {
  innerRef?: Ref<HTMLDivElement>;
  trackingIdBase?: string;
  containerStyles?: Interpolation<Theme>;
  shadowBuffer?: number;
}

interface CarouselContentProps extends BaseCarouselProps {
  children: ReactNode;
}

const CarouselContent: FC<CarouselContentProps> = ({
  children,
  trackingIdBase,
  containerStyles,
  innerRef,
  shadowBuffer = 0,
}) => {
  const { activeSlides, slideHeights } = useBannerCarouselContext();
  const { activeId, setActiveId } = useSlideCarousel();
  const { t } = useTranslation('highValueAdoption');

  const { sortedActiveSlides, eligibleChildren, nonEligibleChildren } = useMemo(() => {
    const eligible: BannerCarouselSlideElement[] = [];
    const nonEligible: BannerCarouselSlideElement[] = [];

    // These are the components which are declared as children of the BannerCarousel and have a valid "name" prop.
    const declaredBanners = Children.toArray(children).filter((child): child is BannerCarouselSlideElement => {
      if (!isValidElement(child)) return false;
      const props = child.props as ComponentProps<typeof BannerCarouselSlide>;
      return typeof props?.name === 'string';
    });

    const sortedActive: string[] = [];

    declaredBanners.forEach((child) => {
      const childName = child.props.name;
      if (activeSlides.includes(childName)) {
        sortedActive.push(childName);
        eligible.push(child);
      } else {
        nonEligible.push(child);
      }
    });

    return {
      sortedActiveSlides: sortedActive,
      eligibleChildren: eligible,
      nonEligibleChildren: nonEligible,
    };
  }, [children, activeSlides]);

  const prevHighestPriorityRef = useRef<string | undefined>(undefined);

  // Auto-focus on highest-priority slide if a NEW higher-priority one registers later
  useEffect(() => {
    const highestPriority = sortedActiveSlides[0];
    const prevHighestPriority = prevHighestPriorityRef.current;
    prevHighestPriorityRef.current = highestPriority;

    if (highestPriority && highestPriority !== prevHighestPriority) {
      // Only force change if the actual highest priority slide has changed (i.e. a higher-priority slide registered)
      setActiveId(highestPriority);
    }
  }, [sortedActiveSlides, setActiveId]);

  // Find index of currently active slide
  const activeIndex = sortedActiveSlides.indexOf(activeId);
  const currentSlideIndex = activeIndex !== -1 ? activeIndex : 0;

  useBannerCarouselTracking({
    activeSlides: sortedActiveSlides,
    currentSlideIndex,
    trackingId: `${trackingIdBase}:banner-impression`,
  });

  const handlePrev = () => {
    const prevIndex = currentSlideIndex === 0 ? sortedActiveSlides.length - 1 : currentSlideIndex - 1;
    const prevId = sortedActiveSlides[prevIndex];
    if (prevId) setActiveId(prevId);
  };

  const handleNext = () => {
    const nextIndex = currentSlideIndex === sortedActiveSlides.length - 1 ? 0 : currentSlideIndex + 1;
    const nextId = sortedActiveSlides[nextIndex];
    if (nextId) setActiveId(nextId);
  };

  const activeHeight = slideHeights[activeId] || 120; // Default fallback height

  return (
    <motion.div
      layout
      css={[
        css`
          display: flex;
          flex-direction: column;
        `,
        containerStyles,
      ]}
      ref={innerRef}
    >
      {/* 
        This wrapper is isolated using position: relative and z-index: 0
        to create an independent stacking context. This ensures that the slide panel
        zIndex values (isCurrentlyActive ? total_count : 1) never bleed out of the carousel.
      */}
      <div
        css={[
          css`
            position: relative;
            z-index: 0;
            overflow: hidden;
            width: calc(100% + ${shadowBuffer * 2}px);
            margin-left: -${shadowBuffer}px;
            transition: height 0.3s ease;
            height: ${activeHeight + shadowBuffer * 2}px;
          `,
        ]}
      >
        <SlideCarousel.Container
          css={css`
            overflow-y: hidden;
          `}
        >
          {eligibleChildren.map((child) => {
            const childName = child.props.name;
            const isCurrentlyActive = childName === activeId;
            // Active slide gets the highest z-index scaled dynamically based on slide count.
            // Any other sliding in lower-priority banners will animate behind the active one.
            const zIndex = isCurrentlyActive ? eligibleChildren.length + 1 : 1;
            return (
              <div
                key={childName}
                css={css`
                  z-index: ${zIndex};
                  position: absolute;
                  width: calc(100% - ${shadowBuffer * 2}px);
                  height: 100%;
                  top: ${shadowBuffer}px;
                  left: ${shadowBuffer}px;
                `}
              >
                <SlideCarousel.Panel id={childName}>{child}</SlideCarousel.Panel>
              </div>
            );
          })}
        </SlideCarousel.Container>
      </div>

      {/* 
        Keep non-eligible children mounted with display: none so they are still
        parsed in the React lifecycle, triggering registerSlide and unregisterSlide.
      */}
      <div
        css={css`
          display: none;
        `}
      >
        {nonEligibleChildren}
      </div>

      {/* Render arrows + dots navigation centered below the banner */}
      {sortedActiveSlides.length > 1 && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            gap: var(--fabric-space-padding-xs);
            margin-top: var(--fabric-space-padding-xs);
          `}
        >
          <Button
            iconName='caret-left'
            variant='secondary'
            aria-label={t('Previous')}
            onClick={handlePrev}
            trackingId={trackingIdBase ? `${trackingIdBase}:prev-btn` : undefined}
          />

          <div
            css={css`
              display: flex;
              gap: var(--fabric-space-padding-xxs);
              align-items: center;
            `}
          >
            {sortedActiveSlides.map((slide, index) => (
              <div
                key={slide}
                css={css`
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  background-color: ${index === currentSlideIndex
                    ? 'var(--fabric-color-neutral-90)'
                    : 'var(--fabric-color-neutral-30)'};
                  transition: background-color 0.2s ease;
                  cursor: pointer;
                `}
                onClick={() => setActiveId(slide)}
              />
            ))}
          </div>

          <Button
            iconName='caret-right'
            variant='secondary'
            aria-label={t('Next')}
            onClick={handleNext}
            trackingId={trackingIdBase ? `${trackingIdBase}:next-btn` : undefined}
          />
        </div>
      )}
    </motion.div>
  );
};

interface BannerCarouselProps extends BaseCarouselProps {
  children: BannerCarouselSlideElement | BannerCarouselSlideElement[];
}

const CarouselInner: React.FC<BannerCarouselProps> = ({
  children,
  trackingIdBase,
  containerStyles,
  innerRef,
  shadowBuffer = 0,
}) => {
  const { activeSlides } = useBannerCarouselContext();

  // Sort activeSlides based on JSX children declaration order to guarantee correct priority rendering
  const sortedActiveSlides = useMemo(() => {
    const declaredNames = Children.toArray(children)
      .filter((child): child is BannerCarouselSlideElement => {
        if (!isValidElement(child)) return false;
        const props = child.props as ComponentProps<typeof BannerCarouselSlide>;
        return typeof props?.name === 'string';
      })
      .map((child) => child.props.name);

    return declaredNames.filter((name) => activeSlides.includes(name));
  }, [children, activeSlides]);

  if (sortedActiveSlides.length === 0) {
    return (
      <div
        css={css`
          display: none;
        `}
      >
        {children}
      </div>
    );
  }

  return (
    <SlideCarousel slides={sortedActiveSlides} initialSlide={sortedActiveSlides[0]} slideGap={2 * shadowBuffer}>
      <CarouselContent
        innerRef={innerRef}
        trackingIdBase={trackingIdBase}
        containerStyles={containerStyles}
        shadowBuffer={shadowBuffer}
      >
        {children}
      </CarouselContent>
    </SlideCarousel>
  );
};

interface BannerCarouselNamespace extends React.FC<BannerCarouselProps> {
  Slide: typeof BannerCarouselSlide;
}

export const BannerCarousel: BannerCarouselNamespace = ({ children, ...props }) => {
  return (
    <BannerCarouselProvider>
      <CarouselInner {...props}>{children}</CarouselInner>
    </BannerCarouselProvider>
  );
};

BannerCarousel.Slide = BannerCarouselSlide;
